import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from '../../core/api/auth.service';
import { Subject, takeUntil } from 'rxjs';
import { MenuItem, MessageService } from 'primeng/api';
import { BtrBookingService } from '../../core/services/btr-booking.service';
import { CountryGroupItem, ReadCountryHotelGroupListResponse } from '../../core/api/api.models';
import { SearchAvailabilityQuery } from '../../core/models/booking-model';
import { SelectedSiteTourInfo } from '../../data/interface';
import { APP_CONFIG } from '../../core/config/app-config.service';
import { IAppConfig, SupportTheme } from '../../core/config/app-config.model';
import { is_url } from '../../utils/utils';

@Component({
  selector: 'app-header',
  standalone: false,
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.sass']
})
export class HeaderComponent implements OnInit, OnDestroy {
  constructor(
    private router: Router,
    private auth: AuthService,
    private BtrBookingService: BtrBookingService,
    private messageService: MessageService,
    @Inject(APP_CONFIG) protected config: IAppConfig, 
  ){

  }

  destroyed = new Subject();
  isLoggedIn: boolean = false;
  public userMenus: MenuItem[] = [];

  res: ReadCountryHotelGroupListResponse | undefined;
  hotels: CountryGroupItem[] = [];
  inspectionHotels: CountryGroupItem[] = [];
  selectedHotel: CountryGroupItem | undefined;

  bookInspectionModalState = {
    visible: false,
    isOpenFromDetail: false
  }

  loginModalState = {
    visible: false
  }

  selectedSiteTourInfo?: SelectedSiteTourInfo | null;
  theme?: SupportTheme;
  currentProperty: CountryGroupItem | undefined;


  ngOnInit(): void {
    this.isLoggedIn = this.auth.isLoggedIn();
    this.theme = this.config.themeDefault;

    this.selectedSiteTourInfo = this.BtrBookingService.getSelectedSiteTourInfo();

    this.auth.onAuthChanged().pipe(takeUntil(this.destroyed)).subscribe((profileID) => {
      this.isLoggedIn = profileID ? true : false;
      this.initMenu();
      this.showPreviousBookSiteTour();
    });
    
    this.initMenu();
    this.getHotelGroupList();

    this.BtrBookingService.onPropertyChanged().subscribe(property => {
      this.currentProperty = property;
    });
  }

  initMenu() {
    this.userMenus = [
      { label: 'Logout', command: () => this.logout(), id: 'my-logout' },
      { label: 'Login', command: () => this.doLogin(), id: 'my-logins' },
      { label: 'Book a tour', command: () => this.bookAnInspection(), id: 'my-book-a-tour' },
    ]

    if (this.auth.isLoggedIn()) {
      this.userMenus = this.userMenus.filter(item => item.id !== 'my-logins')
    } else {
      this.userMenus = this.userMenus.filter(item => item.id !== 'my-logout')
    }
  }

  showPreviousBookSiteTour() {
    if (this.selectedSiteTourInfo && !this.selectedSiteTourInfo.roomType && !this.selectedSiteTourInfo.room && this.auth.isLoggedIn()) {
      this.bookAnInspection();
      this.BtrBookingService.clearSelectedApplicationInfo();
    }
  }

  getHotelGroupList() {
    this.BtrBookingService.readCountryHotelGroupList().pipe(takeUntil(this.destroyed)).subscribe({
      next: (res) => {
        if (res && res.success) {
          this.getHotelGroupListSuccess(res);
        }
      }
    })
  }

  getHotelGroupListSuccess(res: ReadCountryHotelGroupListResponse) {
    this.res = res;
    if (res.countryGroups && res.countryGroups.length) {
      res.countryGroups.forEach(group => {
        if (group.items && group.items.length) {
          group.items.forEach(item => {
            this.hotels.push(item);
          })
        }
      })
    }
    
    if(this.hotels.length){
      this.inspectionHotels = this.hotels.filter(e => !is_url(e.bookSiteTourURL));
    }
  }

  ngOnDestroy(): void {
    this.destroyed.next(null);
  }

  openSearchPage(){
    this.clearExtraHistory();
    // this.router.navigateByUrl('/apartments');
    if(this.hotels.length){
      this.selectHotel(this.hotels[0]);
    }
  }

  selectHotel(hotel: CountryGroupItem) {
    this.selectedHotel = hotel;
    let date = new Date();
    if(hotel.numbersOfDaysAddedToToday){
      date.setDate(date.getDate() + hotel.numbersOfDaysAddedToToday);
    }

    const minimumStartDate = this.BtrBookingService.getMinimumLeaseEndDateConfig(hotel.hotelRefID);
    if(minimumStartDate && date.getTime() < minimumStartDate.getTime()){
      date = minimumStartDate;
    }
    
    const params: SearchAvailabilityQuery = {
      property: hotel?.hotelRefID || '',
      leaseLength: 12,
      fromDate: date,
      bedRooms: '1',
      furnished: 'all',
      priceRange: 'all'
    };

    const queryString = this.BtrBookingService.createSearchAvailabilityQueryString(params);
    this.router.navigateByUrl(`/apartments/results?${queryString}`);
  }

  clearExtraHistory() {
    this.BtrBookingService.clearSelectedApplicationInfo();
  }

  doLogin(){
    const siteTourSelect = this.BtrBookingService.getSelectedSiteTourInfo();
    if(siteTourSelect){
      this.BtrBookingService.clearSelectedApplicationInfo();
    }
    
    this.auth.loginWithIDP(window.location.href);
  }

  logout(){
    this.auth.logout();
  }

  visibleChange(visible: boolean) {
    this.bookInspectionModalState.visible = visible;
  }

  onSubmitInspection() {
    this.bookInspectionModalState.visible = false;
  }

  bookAnInspection() {
    if(this.currentProperty && is_url(this.currentProperty.bookSiteTourURL)){
      window.open(this.currentProperty.bookSiteTourURL, '_blank');
      return;
    }

    this.bookInspectionModalState.visible = true;
  }

  onCloseLoginDialog() {
    this.loginModalState.visible = false;
  }

  getRouterLink() {
    const logoLink = this.config.LogoHeaderLink;
    if(logoLink){
      return logoLink;
    }
    return '';
  }

  checkIsProdEnv(){
    return this.config.production ? true : false;
  }

  checkIsShowEnvIndicator() {
    return !this.checkIsProdEnv() && this.config.showEnvIndicator;
  }

  getEnvName() {
    if(!this.config.apiServer.clientId) return undefined;

    const envName = this.config.apiServer.clientId;
    return envName.charAt(0).toUpperCase() + envName.slice(1);
  }
}
